import React from 'react';
import { useNavigate } from 'react-router-dom';
import './PrivacyPolicy.css';
import {
  BackButton,
  EnterpriseCard,
  EnterpriseGrid,
  EnterpriseHero,
  EnterpriseShell,
  EnterpriseTopBar
} from './ui/EnterpriseLayout';

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <EnterpriseShell>
      <EnterpriseTopBar
        right={
          <>
            <BackButton onClick={() => navigate('/')} />
            <span className="vt-chip vt-chip--subtle">Privacy</span>
          </>
        }
      />

      <EnterpriseHero
        title="Veritage Age Verification Privacy Notice"
        subtitle="Last updated: 19th January 2026"
      />

      <EnterpriseGrid columns={1}>
        <EnterpriseCard>
          <div className="privacy-content">
            <p>
              This Age Verification Privacy Notice ("Notice") explains how Veritage ("we", "us", or "our"), a company registered in Ireland, collects, uses, shares and protects personal data when providing age verification services for our clients ("Client") that use our systems to confirm whether a person meets specified minimum age requirements before accessing products, services, content or features.
            </p>

            <p>
              Veritage is responsible as a data processor on behalf of the Client (the entity requesting you to complete an age check). The Client is the data controller for personal data processed for age verification purposes. As data controller, the Client determines the purposes and means of processing and is responsible for responding to data subject rights requests.
            </p>

            <p>
              For purposes of EU data protection laws (including the GDPR), Veritage operates as a joint data importer/processor acting under instructions from the Client.
            </p>

            <h2>1. What is Veritage Age Verification Service?</h2>
            <p>
              Veritage offers a privacy-focused age verification service that enables websites or apps to confirm whether a person is over or under an age threshold set by the Client. The service is designed to minimise data collection, share only the age result with the Client, and process personal data securely and transiently.
            </p>

            <h2>2. Information We May Collect</h2>
            <p>
              Depending on the age-checking method chosen by the Client, Veritage may collect:
            </p>

            <h3>Age Verification Methods:</h3>
            <ul>
              <li><strong>Facial Age Estimation:</strong> a selfie or facial image used to estimate age.</li>
              <li><strong>Identity Document Verification (IDV):</strong> scanning an official ID document (passport, driver's licence, national ID card) to extract date-of-birth data.</li>
              <li><strong>Digital ID App Check:</strong> using a third-party digital ID or credential that provides age attributes.</li>
              <li><strong>Credit Card Check:</strong> card details to infer age (handled by a payment processor).</li>
              <li><strong>Mobile Provider Check:</strong> phone number and basic contact details to infer age through a third-party provider.</li>
              <li><strong>Database Check:</strong> name, address and date of birth shared with credit or verification data providers.</li>
              <li><strong>Email Address Check:</strong> email metadata used to estimate age.</li>
              <li><strong>Partner Age Check:</strong> age check via a partner identity verifier.</li>
              <li><strong>Reusable Tokens / Keys:</strong> local browser tokens or identity keys for repeat age verification.</li>
            </ul>

            <p>
              Personal data may include, only as necessary:
            </p>
            <ul>
              <li>Face image / selfie</li>
              <li>ID document images and extracted details</li>
              <li>Date of birth or age estimate</li>
              <li>Mobile phone number</li>
              <li>Email address</li>
              <li>Name and address</li>
              <li>Credit card cardholder data (limited)</li>
              <li>Device or browser identifiers</li>
            </ul>

            <p>
              All personal data is processed only according to the Client's configuration and for age verification purposes. We do not use this information for unrelated internal research, marketing, profiling, or advertising purposes.
            </p>

            <h2>3. How We Use Your Personal Data</h2>
            <p>
              We use your data only to:
            </p>
            <ul>
              <li>Perform the age check requested by the Client;</li>
              <li>Provide a result to the Client of whether you are "over" or "under" the required age, or your age in years;</li>
              <li>Improve reliability and security of the verification process.</li>
            </ul>

            <p>
              We do not share unnecessary personal details with the Client — only the outcome of the check (e.g., "18+" / "Under 18").
            </p>

            <h2>4. Data Retention</h2>
            <p>
              Personal information used to perform the age verification is deleted as soon as the check is complete except where the Client requests manual review.
            </p>

            <p>
              In manual review cases, data may be retained for up to 28 days before deletion.
            </p>

            <p>
              The Client may choose to retain the results of age checks for longer; retention of age check results is at the Client's discretion.
            </p>

            <p>
              We comply with EU GDPR data minimisation and storage limitation principles and will retain personal data only as long as necessary for the purposes outlined and to meet legal obligations.
            </p>

            <h2>5. Information Sharing</h2>
            <p>
              Veritage shares:
            </p>

            <h3>With the Client</h3>
            <p>
              Only the result of the age verification check (e.g., over/under age threshold or age in years).
            </p>

            <h3>With Third-Party Providers:</h3>
            <p>
              To conduct certain age checks we share limited personal data with trusted, vetted third-party service providers, including but not limited to:
            </p>
            <ul>
              <li>Identity verification specialists</li>
              <li>Payment processors</li>
              <li>Mobile verification services</li>
              <li>Database age verification partners</li>
            </ul>

            <p>
              These third parties process personal data only to support the age check and are contractually bound to handle it in accordance with GDPR and other applicable law.
            </p>

            <h3>Legal Obligations:</h3>
            <p>
              We may disclose personal data if required by Irish or EU law, regulation, judicial process, or to protect rights, property or safety.
            </p>

            <h2>6. Security and Data Location</h2>
            <p>
              Veritage implements appropriate technical and organisational security measures (encryption, access controls, secure servers) to protect personal data against unauthorised access, disclosure, alteration or destruction.
            </p>

            <p>
              Data may be processed and stored in servers located within the EU or in jurisdictions providing adequate protections consistent with GDPR requirements.
            </p>

            <h2>7. Cookies and Similar Technologies</h2>
            <p>
              We may use necessary cookies or similar technologies to operate the age verification service and manage session information. These do not contain directly identifiable personal data.
            </p>

            <h2>8. Your Rights Under GDPR</h2>
            <p>
              You have rights under EU data protection law, including:
            </p>
            <ul>
              <li>Right of access to your personal data;</li>
              <li>Right to rectification or erasure;</li>
              <li>Right to restriction of processing;</li>
              <li>Right to data portability;</li>
              <li>Right to lodge a complaint with a supervisory authority.</li>
            </ul>

            <p>
              As the age check Controller, the Client is responsible for responding to your rights requests. You should contact the Client that requested the age verification for queries about your personal data processed for that check.
            </p>

            <h2>9. Contact Information</h2>
            <p>
              If you have questions about this Privacy Notice or our age verification practices, you may contact:
            </p>
            <p>
              <strong>Veritage</strong><br />
              Email: <a href="mailto:privacy@veritage.io">privacy@veritage.io</a><br />
              Address: Spaces, Suite 186, Donnybrook House, Dublin D04 WN59, Ireland
            </p>

            <h2>10. Updates to this Notice</h2>
            <p>
              We may update this Privacy Notice to reflect changes in our practices or legal requirements. The updated version will be published with a revised "Last updated" date.
            </p>
          </div>
        </EnterpriseCard>
      </EnterpriseGrid>
    </EnterpriseShell>
  );
}

export default PrivacyPolicy;
